import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import {
  Archive,
  FileText,
  Folder,
  History,
  Pencil,
  Camera,
  ZoomIn,
  X,
} from 'lucide-react'
import { PageHeader } from '@/components/layout/PageHeader'
import { Breadcrumb } from '@/components/layout/Breadcrumb'
import { Button } from '@/components/ui/Button'
import { Toast } from '@/components/ui/Toast'
import {
  PageError,
  PageLoading,
  PageNotFound,
} from '@/components/ui/PageAsyncState'
import { MultiSelectChips } from '@/components/forms/MultiSelectChips'
import { FileExplorer } from '@/components/files/FileExplorer'
import { SopViewer } from '@/components/files/SopViewer'
import { PRODUCT_FOLDERS, getFolderBySlug } from '@/config/folders'
import {
  catalogService,
  type CatalogItem,
  type CatalogSopFile,
} from '@/services/catalog'
import {
  formatDimensionInch,
  inchToMm,
  productService,
  roundMmUp,
  type Product,
  type ProductStatus,
} from '@/services/products'
import { listCustomersApi } from '@/services/customers/apiClient'
import type { Customer } from '@/services/customers/types'
import { formatDocumentError } from '@/utils/documentErrorMessages'

// New Unified BOM Components & Storage
import { ProductBomConfigTable } from '@/features/boms/components/ProductBomConfigTable'
import { ProductBomSpreadsheetView } from '@/features/boms/components/ProductBomSpreadsheetView'
import { ProductFoamBomConfigTable } from '@/features/boms/components/ProductFoamBomConfigTable'
import { ProductFoamBomSpreadsheetView } from '@/features/boms/components/ProductFoamBomSpreadsheetView'
import type {
  BomConfigRow,
  ProductBomSpreadsheetData,
} from '@/features/boms/types/bomSpreadsheetTypes'
import type {
  FoamBomConfig,
  FoamBomLine,
} from '@/features/boms/types/foamBomTypes'
import {
  getFoamAssemblyLabel,
  getFoamPackagingLabel,
} from '@/features/boms/types/foamBomTypes'
import {
  loadProductBomConfigs,
  saveProductBomConfigs,
  loadProductBomData,
  saveProductBomData,
} from '@/features/boms/services/bomSpreadsheetStorage'
import {
  getProductBomConfigsApi,
  saveProductBomConfigsApi,
  getProductBomDataApi,
  saveProductBomDataApi,
} from '@/services/boms/productBomSpreadsheetApiClient'
import {
  getProductFoamBomConfigsApi,
  saveProductFoamBomConfigsApi,
  getProductFoamBomDataApi,
} from '@/services/boms/productFoamBomApiClient'

type Draft = {
  name: string
  customerId: string | null
  selectedCustomer: Customer | null
  w: string
  d: string
  h: string
  status: ProductStatus
  note: string
  colorIds: string[]
  woodIds: string[]
  veneerIds: string[]
}

function toDraft(p: Product, customersList: Customer[]): Draft {
  const currentCust = p.customerId
    ? customersList.find((c) => c.id === p.customerId) ||
      (p.customer
        ? {
            id: p.customer.id,
            code: p.customer.code,
            name: p.customer.name,
            status: p.customer.status as any,
            buyer_count: 0,
            open_order_count: 0,
            note: null,
          }
        : null)
    : null

  return {
    name: p.name,
    customerId: p.customerId ?? null,
    selectedCustomer: currentCust,
    w: String(p.w),
    d: String(p.d),
    h: String(p.h),
    status: p.status,
    note: p.note,
    colorIds: [...p.colorIds],
    woodIds: [...p.woodIds],
    veneerIds: [...p.veneerIds],
  }
}

const fieldInput =
  'w-full rounded-[var(--radius-sm)] border border-[var(--color-border)] bg-white px-2 py-1.5 text-sm outline-none focus:border-[var(--color-accent)]'

type AttributeCard = {
  id: string
  name: string
  code: string
  note: string
  sopFiles: CatalogSopFile[]
}

function collectAttributeCards(items: CatalogItem[]): AttributeCard[] {
  return items
    .map((item) => ({
      id: item.id,
      name: item.name,
      code: item.code,
      note: (item.note ?? '').trim(),
      sopFiles: item.sopFiles ?? [],
    }))
    .filter((item) => item.note || item.sopFiles.length > 0)
}

function AttributeNotesPanel({
  title,
  items,
  onOpenSop,
}: {
  title: string
  items: CatalogItem[]
  onOpenSop: (files: CatalogSopFile[], activeId: string) => void
}) {
  const cards = collectAttributeCards(items)
  if (!cards.length) return null

  const dense = cards.length >= 3
  const gridClass =
    cards.length >= 5
      ? 'grid grid-cols-1 gap-1.5 sm:grid-cols-2 xl:grid-cols-3'
      : cards.length >= 3
        ? 'grid grid-cols-1 gap-1.5 sm:grid-cols-2'
        : 'space-y-1.5'

  return (
    <div
      className={`rounded-[var(--radius-sm)] border border-amber-100 bg-amber-50/60 ${
        dense ? 'px-2 py-1.5' : 'px-3 py-2'
      }`}
    >
      <div className="mb-1 text-xs font-semibold tracking-wide text-amber-900 uppercase">
        {title}
        <span className="ml-1 font-normal text-amber-700/70 normal-case">
          ({cards.length})
        </span>
      </div>
      <ul className={gridClass}>
        {cards.map((item) => (
          <li
            key={item.id}
            className={`text-xs text-amber-950 ${
              dense
                ? 'rounded border border-amber-100/80 bg-white/70 px-2 py-1.5'
                : ''
            }`}
          >
            <div className="flex flex-wrap items-baseline gap-x-1">
              <span className="font-semibold text-[var(--color-accent)]">
                {item.name}
              </span>
              <span className="text-slate-400">· {item.code}</span>
            </div>
            {item.note ? (
              <div
                className={`mt-0.5 text-slate-700 ${
                  dense ? 'line-clamp-3' : ''
                }`}
              >
                {item.note}
              </div>
            ) : null}
            {item.sopFiles.length ? (
              <div className="mt-1 flex flex-wrap gap-1">
                {item.sopFiles.map((file) => (
                  <button
                    key={file.id}
                    type="button"
                    className="inline-flex max-w-full items-center gap-1 rounded bg-white/90 px-1.5 py-0.5 text-xs font-medium text-[var(--color-link)] ring-1 ring-amber-100 hover:bg-amber-50 cursor-pointer"
                    title={`Xem: ${file.name}`}
                    onClick={() => onOpenSop(item.sopFiles, file.id)}
                  >
                    <FileText className="h-3 w-3 shrink-0" />
                    <span className="truncate max-w-[140px]">{file.name}</span>
                  </button>
                ))}
                {item.sopFiles.length > 1 ? (
                  <span className="self-center text-xs text-slate-400">
                    · xem trong trang
                  </span>
                ) : null}
              </div>
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  )
}

const SAMPLE_BED_PHOTO =
  'data:image/svg+xml;charset=utf-8,' +
  encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" width="900" height="640" viewBox="0 0 900 640">
  <defs>
    <linearGradient id="wallBg" x1="0%" y1="0%" x2="0%" y2="100%">
      <stop offset="0%" stop-color="#f8fafc"/>
      <stop offset="60%" stop-color="#e2e8f0"/>
      <stop offset="100%" stop-color="#cbd5e1"/>
    </linearGradient>
    <linearGradient id="walnutFinish" x1="0%" y1="0%" x2="100%" y2="100%">
      <stop offset="0%" stop-color="#5a2d0c"/>
      <stop offset="50%" stop-color="#7c3f14"/>
      <stop offset="100%" stop-color="#451e06"/>
    </linearGradient>
    <linearGradient id="grainSide" x1="0%" y1="0%" x2="100%" y2="0%">
      <stop offset="0%" stop-color="#8a4d1f"/>
      <stop offset="30%" stop-color="#6f3b14"/>
      <stop offset="60%" stop-color="#935322"/>
      <stop offset="100%" stop-color="#6f3b14"/>
    </linearGradient>
    <linearGradient id="mattressStyle" x1="0%" y1="0%" x2="0%" y2="100%">
      <stop offset="0%" stop-color="#ffffff"/>
      <stop offset="80%" stop-color="#f1f5f9"/>
      <stop offset="100%" stop-color="#e2e8f0"/>
    </linearGradient>
  </defs>
  <rect width="900" height="640" fill="url(#wallBg)"/>
  <rect y="460" width="900" height="180" fill="#b45309" opacity="0.12"/>
  <line x1="0" y1="460" x2="900" y2="460" stroke="#92400e" stroke-width="2" opacity="0.4"/>
  <g>
    <rect x="120" y="70" width="660" height="300" rx="10" fill="url(#walnutFinish)" stroke="#270e02" stroke-width="3"/>
    <line x1="120" y1="145" x2="780" y2="145" stroke="#3d1803" stroke-width="2"/>
    <line x1="120" y1="220" x2="780" y2="220" stroke="#3d1803" stroke-width="2"/>
    <line x1="120" y1="295" x2="780" y2="295" stroke="#3d1803" stroke-width="2"/>
    <rect x="145" y="90" width="610" height="260" rx="6" fill="none" stroke="#ca8a04" stroke-width="1.5" stroke-opacity="0.3"/>
    <rect x="145" y="270" width="610" height="165" rx="16" fill="url(#mattressStyle)" stroke="#cbd5e1" stroke-width="2"/>
    <path d="M 145 365 Q 450 380 755 365 L 755 435 L 145 435 Z" fill="#1e40af" opacity="0.88"/>
    <rect x="180" y="235" width="160" height="75" rx="14" fill="#ffffff" stroke="#cbd5e1" stroke-width="1.5"/>
    <rect x="370" y="235" width="160" height="75" rx="14" fill="#ffffff" stroke="#cbd5e1" stroke-width="1.5"/>
    <rect x="560" y="235" width="160" height="75" rx="14" fill="#ffffff" stroke="#cbd5e1" stroke-width="1.5"/>
    <rect x="135" y="425" width="630" height="95" rx="6" fill="url(#grainSide)" stroke="#270e02" stroke-width="3"/>
    <rect x="165" y="442" width="175" height="60" rx="4" fill="#5a2d0c" stroke="#270e02" stroke-width="2"/>
    <circle cx="252" cy="472" r="5.5" fill="#facc15" stroke="#854d0e" stroke-width="1.5"/>
    <rect x="362" y="442" width="176" height="60" rx="4" fill="#5a2d0c" stroke="#270e02" stroke-width="2"/>
    <circle cx="450" cy="472" r="5.5" fill="#facc15" stroke="#854d0e" stroke-width="1.5"/>
    <rect x="560" y="442" width="175" height="60" rx="4" fill="#5a2d0c" stroke="#270e02" stroke-width="2"/>
    <circle cx="647" cy="472" r="5.5" fill="#facc15" stroke="#854d0e" stroke-width="1.5"/>
    <rect x="135" y="520" width="40" height="32" rx="3" fill="#270e02"/>
    <rect x="725" y="520" width="40" height="32" rx="3" fill="#270e02"/>
  </g>
  <rect x="25" y="25" width="240" height="36" rx="6" fill="rgba(15,23,42,0.85)"/>
  <text x="38" y="49" fill="#ffffff" font-family="'Inter', sans-serif" font-size="13" font-weight="bold">ẢNH THỰC TẾ FULL: MP180</text>
</svg>
`)

export function ProductDetailPage() {
  const { productId = '', folder } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()

  const [product, setProduct] = useState<Product | null>(null)
  const [loading, setLoading] = useState(true)
  const [savingImage, setSavingImage] = useState(false)
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState<Draft | null>(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [toastMsg, setToastMsg] = useState<string | null>(null)
  const [colors, setColors] = useState<CatalogItem[]>([])
  const [woods, setWoods] = useState<CatalogItem[]>([])
  const [workspaceSlug, setWorkspaceSlug] = useState(
    folder && getFolderBySlug(folder) ? folder : PRODUCT_FOLDERS[0].slug,
  )
  const [sopPreview, setSopPreview] = useState<{
    files: CatalogSopFile[]
    activeId: string
  } | null>(null)
  const [customers, setCustomers] = useState<Customer[]>([])

  // Dimensions: Inch & MM synchronization
  const [dimInch, setDimInch] = useState({ w: 70.87, d: 78.74, h: 45.28 })
  const [dimMm, setDimMm] = useState({ w: 1800, d: 2000, h: 1150 })
  const [draftColorNotes, setDraftColorNotes] = useState<Record<string, string>>({})

  // BOM Configuration & Spreadsheet View State
  const [bomConfigs, setBomConfigs] = useState<BomConfigRow[]>([])
  const [selectedBomConfig, setSelectedBomConfig] = useState<BomConfigRow | null>(null)
  const [bomData, setBomData] = useState<ProductBomSpreadsheetData | null>(null)

  // Foam BOM Configuration & Spreadsheet View State
  const [foamBomConfigs, setFoamBomConfigs] = useState<FoamBomConfig[]>([])
  const [selectedFoamBomConfig, setSelectedFoamBomConfig] = useState<FoamBomConfig | null>(null)
  const [foamBomLines, setFoamBomLines] = useState<FoamBomLine[]>([])

  const [viewMode, setViewMode] = useState<'product' | 'bom-spreadsheet' | 'foam-bom-spreadsheet'>(
    (() => { const v = searchParams.get('view'); if (v === 'bom') return 'bom-spreadsheet'; if (v === 'foam-bom') return 'foam-bom-spreadsheet'; return 'product'; })(),
  )

  // Image upload & Lightbox
  const [lightboxImg, setLightboxImg] = useState<string | null>(null)
  const [isDragOver, setIsDragOver] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (folder && getFolderBySlug(folder)) setWorkspaceSlug(folder)
  }, [folder])

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape' && lightboxImg) {
        setLightboxImg(null)
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [lightboxImg])

  useEffect(() => {
    void listCustomersApi({ status: 'active' })
      .then((list) => setCustomers(list.filter((c) => c.status === 'active')))
      .catch((err) => console.error('Failed to load customers', err))
  }, [])

  useEffect(() => {
    void Promise.all([
      catalogService.list('color', true),
      catalogService.list('wood', true),
    ]).then(([c, w]) => {
      setColors(c)
      setWoods(w)
    })
  }, [])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setEditing(false)
    setDraft(null)
    setError(null)
    // Drop previous product's BOM immediately — never carry selection/data across productId.
    setBomConfigs([])
    setSelectedBomConfig(null)
    setBomData(null)
    if (!searchParams.get('view')) {
      setViewMode('product')
    }
    productService.getById(productId).then((result) => {
      if (cancelled) return
      setProduct(result)
      setLoading(false)
      if (result) {
        // Init dimensions
        const mmW = result.w ? roundMmUp(result.w) : 1800
        const mmD = result.d ? roundMmUp(result.d) : 2000
        const mmH = result.h ? roundMmUp(result.h) : 1150
        setDimMm({ w: mmW, d: mmD, h: mmH })
        setDimInch({
          w: formatDimensionInch(result.w) || 70.87,
          d: formatDimensionInch(result.d) || 78.74,
          h: formatDimensionInch(result.h) || 45.28,
        })

        // Load BOM configs & data (sync fallback first, then async backend)
        const loadedConfigs = loadProductBomConfigs(result.id, result.code)
        setBomConfigs(loadedConfigs)
        setSelectedBomConfig(loadedConfigs.length > 0 ? loadedConfigs[0] : null)
        const loadedData = loadProductBomData(result.id, result.code, result.name)
        setBomData(loadedData)

        // Async fetch from Postgres backend
        getProductBomConfigsApi(result.id, result.code)
          .then(async (apiConfigs) => {
            if (cancelled) return
            if (apiConfigs.length === 0) {
              setBomConfigs([])
              setSelectedBomConfig(null)
              setBomData(
                loadProductBomData(result.id, result.code, result.name),
              )
              return
            }
            setBomConfigs(apiConfigs)
            const targetConfigId = searchParams.get('config')
            const activeCfg = (targetConfigId ? apiConfigs.find((c) => c.id === targetConfigId) : null) || apiConfigs[0]
            try {
              const apiData = await getProductBomDataApi(result.id, activeCfg.id)
              if (cancelled) return
              setBomData({
                productCode: result.code,
                productName: result.name,
                configCode: activeCfg.code,
                wood: apiData?.wood ?? [],
                hardware: apiData?.hardware ?? [],
              })
              setSelectedBomConfig(activeCfg)
            } catch {
              if (cancelled) return
              // Never keep another product's rows — clear to empty for this product/config.
              setBomData({
                productCode: result.code,
                productName: result.name,
                configCode: activeCfg.code,
                wood: [],
                hardware: [],
              })
              setSelectedBomConfig(activeCfg)
              setError(
                `Không tải được dữ liệu BOM cấu hình «${activeCfg.code}» từ máy chủ. Không dùng dữ liệu BOM cũ.`,
              )
            }
          })
          .catch(() => {
            if (cancelled) return
            // Sync fallback already applied above; surface failure instead of silent keep.
            setError(
              'Không tải được cấu hình BOM từ máy chủ. Đang dùng bản cache cục bộ (nếu có) của sản phẩm này.',
            )
          })

        // Load Foam BOM configs from Postgres backend
        getProductFoamBomConfigsApi(result.id)
          .then(async (apiFoamConfigs) => {
            if (cancelled) return
            setFoamBomConfigs(apiFoamConfigs)
            if (searchParams.get('view') === 'foam-bom') {
              const targetConfigId = searchParams.get('config')
              const cfg = (targetConfigId ? apiFoamConfigs.find((c) => c.id === targetConfigId) : null) || apiFoamConfigs[0]
              if (cfg) {
                try {
                  const loaded = await getProductFoamBomDataApi(result.id, cfg.id)
                  if (!cancelled) {
                    setFoamBomLines(loaded?.lines ?? [])
                    setSelectedFoamBomConfig(cfg)
                  }
                } catch {
                  if (!cancelled) {
                    setFoamBomLines([])
                    setSelectedFoamBomConfig(cfg)
                  }
                }
              }
            }
          })
          .catch((err) => {
            console.error('Failed to load foam bom configs', err)
          })
      }
    })
    return () => {
      cancelled = true
    }
  }, [productId])

  function handleInchChange(key: 'w' | 'd' | 'h', val: number) {
    const nextInch = { ...dimInch, [key]: val }
    setDimInch(nextInch)
    const convertedMm = inchToMm(val)
    const nextMm = { ...dimMm, [key]: convertedMm }
    setDimMm(nextMm)

    if (product) {
      // Sync with product dimensions silently or update draft
      if (draft) {
        setDraft({ ...draft, [key]: String(convertedMm) })
      }
    }
  }

  function handleImageUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file || !product) return
    const reader = new FileReader()
    reader.onload = () => {
      const dataUrl = reader.result as string
      void handleImageChange(dataUrl)
    }
    reader.readAsDataURL(file)
  }

  function handleDropImage(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault()
    setIsDragOver(false)
    const file = e.dataTransfer.files?.[0]
    if (!file || !product) return
    const reader = new FileReader()
    reader.onload = () => {
      const dataUrl = reader.result as string
      void handleImageChange(dataUrl)
    }
    reader.readAsDataURL(file)
  }

  function startEdit() {
    if (!product) return
    setDraft(toDraft(product, customers))
    setDraftColorNotes(
      Object.fromEntries(colors.map((c) => [c.id, c.note || ''])),
    )
    setEditing(true)
    setError(null)
  }

  function cancelEdit() {
    setEditing(false)
    setDraft(null)
    setDraftColorNotes({})
    setError(null)
    if (product) {
      const mmW = product.w ? roundMmUp(product.w) : 1800
      const mmD = product.d ? roundMmUp(product.d) : 2000
      const mmH = product.h ? roundMmUp(product.h) : 1150
      setDimMm({ w: mmW, d: mmD, h: mmH })
      setDimInch({
        w: formatDimensionInch(product.w) || 70.87,
        d: formatDimensionInch(product.d) || 78.74,
        h: formatDimensionInch(product.h) || 45.28,
      })
    }
  }

  async function saveEdit() {
    if (!product || !draft) return
    setSaving(true)
    setError(null)
    try {
      const wMm = inchToMm(dimInch.w) || roundMmUp(Number(draft.w)) || 0
      const dMm = inchToMm(dimInch.d) || roundMmUp(Number(draft.d)) || 0
      const hMm = inchToMm(dimInch.h) || roundMmUp(Number(draft.h)) || 0
      const updated = await productService.update(product.id, {
        name: draft.name,
        customerId: draft.customerId,
        w: wMm,
        d: dMm,
        h: hMm,
        status: draft.status,
        note: draft.note,
        colorIds: draft.colorIds,
        woodIds: draft.woodIds,
        veneerIds: draft.veneerIds,
      })

      if (draft.customerId) {
        const found = customers.find((c) => c.id === draft.customerId)
        if (found) {
          updated.customer = {
            id: found.id,
            code: found.code,
            name: found.name,
            status: found.status,
          }
        }
      } else {
        updated.customer = null
      }

      // Update modified color notes in catalog
      const modifiedColorNotes = Object.entries(draftColorNotes).filter(([id, note]) => {
        const orig = colors.find((c) => c.id === id)
        return orig && (orig.note || '') !== note.trim()
      })

      if (modifiedColorNotes.length > 0) {
        await Promise.all(
          modifiedColorNotes.map(([id, note]) =>
            catalogService.update('color', id, { note: note.trim() }),
          ),
        )
        setColors((prev) =>
          prev.map((c) => {
            const match = modifiedColorNotes.find(([id]) => id === c.id)
            return match ? { ...c, note: match[1].trim() } : c
          }),
        )
      }

      setProduct(updated)
      setEditing(false)
      setDraft(null)
      setDraftColorNotes({})
      setToastMsg('✓ Đã lưu thông tin sản phẩm thành công!')
    } catch (err) {
      const formatted = formatDocumentError(err)
      setError(formatted.message)
    } finally {
      setSaving(false)
    }
  }

  async function handleImageChange(dataUrl: string | undefined) {
    if (!product) return
    setSavingImage(true)
    try {
      const updated = await productService.update(product.id, {
        imageUrl: dataUrl,
      })
      setProduct(updated)
    } finally {
      setSavingImage(false)
    }
  }

  const workspace = getFolderBySlug(workspaceSlug) ?? PRODUCT_FOLDERS[0]
  const view = editing && draft ? draft : null

  const selectedColors = useMemo(() => {
    if (!product) return []
    const ids = view ? view.colorIds : product.colorIds
    return colors.filter((c) => ids.includes(c.id))
  }, [product, view, colors])

  const selectedWoods = useMemo(() => {
    if (!product) return []
    const ids = view ? view.woodIds : product.woodIds
    return woods.filter((c) => ids.includes(c.id))
  }, [product, view, woods])

  const masterCardsCount = useMemo(() => {
    return (
      collectAttributeCards(selectedColors).length +
      collectAttributeCards(selectedWoods).length
    )
  }, [selectedColors, selectedWoods])

  const effectiveColorNotes = useMemo(() => {
    const lines = selectedColors
      .map((c) => {
        const n = (editing ? (draftColorNotes[c.id] ?? c.note ?? '') : (c.note ?? '')).trim()
        return n ? `${c.name}: ${n}` : null
      })
      .filter(Boolean)
    return lines.join(' | ')
  }, [selectedColors, editing, draftColorNotes])

  function openSopView(files: CatalogSopFile[], activeId: string) {
    setSopPreview({ files, activeId })
  }

  if (loading) {
    return <PageLoading label="Đang tải..." />
  }

  if (!product) {
    return (
      <PageNotFound
        title="Không tìm thấy mã hàng"
        description="product_id không tồn tại."
        action={
          <Button variant="secondary" onClick={() => navigate('/products')}>
            Quay lại danh sách
          </Button>
        }
      />
    )
  }

  // ---------------- VIEW 2: FULL BOM SPREADSHEET VIEW ----------------
  if (viewMode === 'bom-spreadsheet' && selectedBomConfig && bomData) {
    return (
      <ProductBomSpreadsheetView
        key={`${product.id}-${selectedBomConfig.id}`}
        productId={product.id}
        productCode={product.code}
        productName={product.name}
        productImageUrl={product.imageUrl}
        customer={
          product.customer
            ? product.customer.code
            : (customers.find((c) => c.id === product.customerId)?.code ?? '—')
        }
        dimensionsMm={dimMm}
        dimensionsInch={dimInch}
        colorNotes={effectiveColorNotes}
        activeConfig={selectedBomConfig}
        allConfigs={bomConfigs}
        initialData={bomData}
        onSaveData={async (newData) => {
          setBomData(newData)
          saveProductBomData(product.id, newData)
          if (selectedBomConfig) {
            await saveProductBomDataApi(product.id, selectedBomConfig.id, {
              wood: newData.wood,
              hardware: newData.hardware,
            }).catch(() => {})
          }
        }}
        onBack={() => {
          setViewMode('product')
          setSearchParams({})
        }}
      />
    )
  }

  if (viewMode === 'foam-bom-spreadsheet' && !selectedFoamBomConfig) {
    return <PageLoading />
  }

  // ---------------- VIEW 3: FOAM BOM SPREADSHEET VIEW ----------------
  if (viewMode === 'foam-bom-spreadsheet' && selectedFoamBomConfig) {
    return (
      <ProductFoamBomSpreadsheetView
        key={`${product.id}-${selectedFoamBomConfig.id}`}
        productId={product.id}
        productCode={product.code}
        productName={product.name}
        productImageUrl={product.imageUrl}
        customer={
          product.customer
            ? product.customer.code
            : (customers.find((c) => c.id === product.customerId)?.code ?? '—')
        }
        config={selectedFoamBomConfig}
        initialLines={foamBomLines}
        onBack={() => {
          setViewMode('product')
          setSelectedFoamBomConfig(null)
          setFoamBomLines([])
          setSearchParams({})
        }}
      />
    )
  }

  // ---------------- VIEW 1: PRODUCT DETAIL PAGE ----------------
  return (
    <div>
      <Breadcrumb
        items={[
          { label: 'Trang chủ', to: '/' },
          { label: 'Mã hàng', to: '/products' },
          { label: product.code },
        ]}
      />

      <PageHeader
        title={`${product.code} — ${product.name}`}
        subtitle="Identity · Attributes · BOM Configurations · Documents"
        actions={
          <div className="flex flex-wrap gap-2">
            {editing ? (
              <>
                <Button variant="secondary" disabled={saving} onClick={cancelEdit}>
                  Hủy
                </Button>
                <Button disabled={saving} onClick={() => void saveEdit()}>
                  {saving ? 'Đang lưu...' : 'Lưu'}
                </Button>
              </>
            ) : (
              <>
                <Button variant="secondary" onClick={startEdit}>
                  <Pencil className="h-4 w-4" />
                  Chỉnh sửa
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => navigate(`/products/${product.id}/change-code`)}
                >
                  <History className="h-4 w-4" />
                  Đổi mã
                </Button>
                <Button
                  variant="secondary"
                  disabled
                  className="opacity-50 cursor-not-allowed text-slate-400 border-slate-200"
                  title="Chức năng lưu trữ đã bị vô hiệu hóa"
                >
                  <Archive className="h-4 w-4" />
                  Lưu trữ
                </Button>
              </>
            )}
          </div>
        }
      />

      {error ? <PageError message={error} /> : null}

      {/* ---------------- CARD IDENTITY (ẢNH SẢN PHẨM LỚN 380PX & THÔNG TIN ĐỒNG BỘ) ---------------- */}
      <div className="mb-4 rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)] p-4 shadow-xs">
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-[380px_1fr] items-stretch">
          {/* CỘT TRÁI: KHUNG ẢNH SẢN PHẨM LỚN (380PX RỘNG, MIN-HEIGHT 420PX, DRAG & DROP, FULL PREVIEW) */}
          <div
            onDragOver={(e) => {
              e.preventDefault()
              setIsDragOver(true)
            }}
            onDragLeave={(e) => {
              e.preventDefault()
              setIsDragOver(false)
            }}
            onDrop={handleDropImage}
            className={`flex flex-col justify-between rounded-lg border-2 border-dashed p-3 transition min-h-[420px] bg-white ${
              isDragOver
                ? 'border-emerald-500 bg-emerald-50/50'
                : 'border-slate-300 hover:border-emerald-500'
            }`}
          >
            <div className="flex-1 flex flex-col items-center justify-center overflow-hidden rounded bg-slate-50 border border-slate-200 relative min-h-[340px]">
              {product.imageUrl ? (
                <img
                  src={product.imageUrl}
                  alt={`Ảnh ${product.name}`}
                  onClick={() => setLightboxImg(product.imageUrl || null)}
                  className="h-full w-full object-contain cursor-zoom-in transition-transform hover:scale-102"
                  title="Click để phóng to toàn màn hình"
                />
              ) : (
                <div className="flex flex-col items-center justify-center p-4 text-center">
                  <svg
                    width="260"
                    height="160"
                    viewBox="0 0 200 120"
                    className="drop-shadow-md mb-2"
                  >
                    <ellipse cx="100" cy="112" rx="88" ry="6" fill="#cbd5e1" opacity="0.6" />
                    <rect x="16" y="8" width="168" height="54" rx="3" fill="#92400e" stroke="#78350f" strokeWidth="1.5" />
                    <line x1="16" y1="25" x2="184" y2="25" stroke="#b45309" strokeWidth="1" />
                    <line x1="16" y1="42" x2="184" y2="42" stroke="#b45309" strokeWidth="1" />
                    <rect x="22" y="45" width="156" height="35" rx="4" fill="#f8fafc" stroke="#cbd5e1" strokeWidth="1.5" />
                    <rect x="32" y="40" width="42" height="19" rx="4" fill="#e2e8f0" stroke="#94a3b8" strokeWidth="1" />
                    <rect x="80" y="40" width="42" height="19" rx="4" fill="#e2e8f0" stroke="#94a3b8" strokeWidth="1" />
                    <rect x="128" y="40" width="36" height="19" rx="4" fill="#e2e8f0" stroke="#94a3b8" strokeWidth="1" />
                    <rect x="18" y="76" width="164" height="26" rx="2" fill="#78350f" stroke="#451a03" strokeWidth="1.5" />
                    <rect x="26" y="81" width="44" height="16" rx="1.5" fill="#92400e" stroke="#451a03" strokeWidth="1" />
                    <circle cx="48" cy="89" r="1.5" fill="#fef08a" />
                    <rect x="76" y="81" width="44" height="16" rx="1.5" fill="#92400e" stroke="#451a03" strokeWidth="1" />
                    <circle cx="98" cy="89" r="1.5" fill="#fef08a" />
                    <rect x="18" y="102" width="14" height="10" fill="#451a03" />
                    <rect x="168" y="102" width="14" height="10" fill="#451a03" />
                  </svg>
                  <div className="font-bold text-slate-800 text-xs">{product.name}</div>
                  <div className="text-[11px] text-slate-400">
                    (Kéo thả ảnh vào đây hoặc bấm nút tải ảnh bên dưới)
                  </div>
                </div>
              )}
            </div>

            {/* Image Action Bar — chỉ hiện khi đang Chỉnh sửa */}
            {editing && (
              <div className="mt-3 flex flex-wrap items-center justify-center gap-1.5">
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={savingImage}
                  className="inline-flex items-center gap-1 rounded bg-emerald-600 px-3 py-1.5 text-xs font-bold text-white shadow-xs hover:bg-emerald-700 transition cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Camera className="h-3.5 w-3.5" />
                  📷 Tải ảnh lên (Full)
                </button>
                <button
                  type="button"
                  onClick={() => setLightboxImg(product.imageUrl || SAMPLE_BED_PHOTO)}
                  className="inline-flex items-center gap-1 rounded border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 transition cursor-pointer"
                  title="Xem ảnh toàn màn hình HD"
                >
                  <ZoomIn className="h-3.5 w-3.5 text-slate-500" />
                  Phóng to
                </button>
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleImageUpload}
            />
          </div>

          {/* CỘT PHẢI: CÁC TRƯỜNG THÔNG TIN SẢN PHẨM (LƯỚI 3 CỘT CÂN ĐỐI) */}
          <div className="flex flex-col gap-3 min-w-0">
            {/* Hàng 1: Mã, Tên, Trạng thái (Khi ấn sửa thì mới được sửa) */}
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
              <div className="rounded border border-slate-200 bg-slate-50/80 px-3 py-2">
                <div className="text-xs font-bold uppercase tracking-wider text-slate-500">
                  Mã hàng
                </div>
                <div className="mt-0.5 text-sm font-black font-mono text-slate-900">
                  {product.code}
                </div>
              </div>

              <div className="rounded border border-slate-200 bg-slate-50/80 px-3 py-2 sm:col-span-1">
                <div className="text-xs font-bold uppercase tracking-wider text-slate-500">
                  Tên hàng
                </div>
                {view ? (
                  <input
                    className={`${fieldInput} mt-0.5`}
                    value={view.name}
                    onChange={(e) => setDraft({ ...view, name: e.target.value })}
                  />
                ) : (
                  <div className="mt-0.5 text-sm font-bold text-slate-800 truncate">
                    {product.name}
                  </div>
                )}
              </div>

              <div className="rounded border border-slate-200 bg-slate-50/80 px-3 py-2">
                <div className="text-xs font-bold uppercase tracking-wider text-slate-500">
                  Trạng thái
                </div>
                {view ? (
                  <select
                    value={view.status}
                    onChange={(e) => setDraft({ ...view, status: e.target.value as ProductStatus })}
                    className="mt-1 w-full rounded border border-slate-300 bg-white px-2 py-1 text-xs font-bold text-slate-800"
                  >
                    <option value="ok">● OK (Hoạt động)</option>
                    <option value="pending">⚡ Đang Cập Nhật</option>
                    <option value="inactive">Ngưng dùng</option>
                  </select>
                ) : (
                  <div className="mt-1">
                    <span
                      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-bold ${
                        product.status === 'ok'
                          ? 'bg-emerald-100 text-emerald-800'
                          : product.status === 'pending'
                            ? 'bg-amber-100 text-amber-800'
                            : 'bg-slate-200 text-slate-700'
                      }`}
                    >
                      {product.status === 'ok'
                        ? '● OK (Hoạt động)'
                        : product.status === 'pending'
                          ? '⚡ Đang Cập Nhật'
                          : 'Ngưng dùng'}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Hàng 2: Khách hàng / Thị trường (Link với khách hàng của Sale, chỉnh sửa khi sửa) */}
            <div className="rounded border border-slate-200 bg-slate-50/80 px-3 py-2">
              <div className="text-xs font-bold uppercase tracking-wider text-slate-500">
                Khách hàng / Thị trường
              </div>
              {view ? (
                <select
                  value={view.customerId || ''}
                  onChange={(e) => {
                    const selectedId = e.target.value || null
                    const selected = customers.find((c) => c.id === selectedId) || null
                    setDraft({
                      ...view,
                      customerId: selectedId,
                      selectedCustomer: selected,
                    })
                  }}
                  className="mt-1 w-full rounded border border-slate-300 bg-white px-2 py-1.5 text-xs font-medium text-slate-900 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                >
                  <option value="">-- Chưa chọn khách hàng (Sale) --</option>
                  {customers.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.code} — {c.name}
                    </option>
                  ))}
                </select>
              ) : (
                <div className="mt-0.5 text-xs font-semibold text-slate-800">
                  {product.customer ? (
                    <span className="text-slate-900 font-bold">
                      {product.customer.code} — {product.customer.name}
                    </span>
                  ) : product.customerId ? (
                    <span className="text-slate-900 font-bold">
                      {customers.find((c) => c.id === product.customerId)?.code ?? product.customerId} —{' '}
                      {customers.find((c) => c.id === product.customerId)?.name ?? ''}
                    </span>
                  ) : (
                    <span>Williams Sonoma / Crate & Barrel (Thị trường Mỹ & EU)</span>
                  )}
                </div>
              )}
            </div>

            {/* Hàng 3: Kích thước nhập là INCH (W, D, H) - Khi ấn sửa thì mới được sửa */}
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
              <div className="rounded border border-emerald-200 bg-emerald-50/60 px-3 py-2">
                <div className="text-xs font-bold uppercase tracking-wider text-emerald-800">
                  W (INCH)
                </div>
                {view ? (
                  <input
                    type="number"
                    step="0.01"
                    value={dimInch.w}
                    onChange={(e) => handleInchChange('w', parseFloat(e.target.value) || 0)}
                    className="mt-0.5 w-full rounded border border-emerald-300 bg-white px-2 py-1 text-sm font-mono font-bold text-slate-900 outline-none focus:ring-1 focus:ring-emerald-500"
                  />
                ) : (
                  <div className="mt-0.5 text-sm font-mono font-bold text-emerald-950">
                    {dimInch.w}"
                  </div>
                )}
              </div>

              <div className="rounded border border-emerald-200 bg-emerald-50/60 px-3 py-2">
                <div className="text-xs font-bold uppercase tracking-wider text-emerald-800">
                  D (INCH)
                </div>
                {view ? (
                  <input
                    type="number"
                    step="0.01"
                    value={dimInch.d}
                    onChange={(e) => handleInchChange('d', parseFloat(e.target.value) || 0)}
                    className="mt-0.5 w-full rounded border border-emerald-300 bg-white px-2 py-1 text-sm font-mono font-bold text-slate-900 outline-none focus:ring-1 focus:ring-emerald-500"
                  />
                ) : (
                  <div className="mt-0.5 text-sm font-mono font-bold text-emerald-950">
                    {dimInch.d}"
                  </div>
                )}
              </div>

              <div className="rounded border border-emerald-200 bg-emerald-50/60 px-3 py-2">
                <div className="text-xs font-bold uppercase tracking-wider text-emerald-800">
                  H (INCH)
                </div>
                {view ? (
                  <input
                    type="number"
                    step="0.01"
                    value={dimInch.h}
                    onChange={(e) => handleInchChange('h', parseFloat(e.target.value) || 0)}
                    className="mt-0.5 w-full rounded border border-emerald-300 bg-white px-2 py-1 text-sm font-mono font-bold text-slate-900 outline-none focus:ring-1 focus:ring-emerald-500"
                  />
                ) : (
                  <div className="mt-0.5 text-sm font-mono font-bold text-emerald-950">
                    {dimInch.h}"
                  </div>
                )}
              </div>
            </div>

            {/* Hàng 4: 3 ô kích thước ở dưới thể hiện MM (Tự động quy đổi) */}
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
              <div className="rounded border border-sky-200 bg-sky-50/60 px-3 py-2">
                <div className="text-xs font-bold uppercase tracking-wider text-sky-800">
                  W (MM)
                </div>
                <div className="mt-0.5 text-sm font-mono font-black text-sky-900">
                  {dimMm.w} mm
                </div>
              </div>

              <div className="rounded border border-sky-200 bg-sky-50/60 px-3 py-2">
                <div className="text-xs font-bold uppercase tracking-wider text-sky-800">
                  D (MM)
                </div>
                <div className="mt-0.5 text-sm font-mono font-black text-sky-900">
                  {dimMm.d} mm
                </div>
              </div>

              <div className="rounded border border-sky-200 bg-sky-50/60 px-3 py-2">
                <div className="text-xs font-bold uppercase tracking-wider text-sky-800">
                  H (MM)
                </div>
                <div className="mt-0.5 text-sm font-mono font-black text-sky-900">
                  {dimMm.h} mm
                </div>
              </div>
            </div>

            {/* Hàng 5: Thuộc tính Màu, Gỗ (Bỏ ô Veneer theo yêu cầu) */}
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <div className="rounded border border-slate-200 px-3 py-2">
                <div className="mb-1 text-xs font-bold uppercase tracking-wider text-slate-500">
                  Màu
                </div>
                <MultiSelectChips
                  variant="chips-plus"
                  options={colors.filter(
                    (c) => c.isActive || selectedColors.some((s) => s.id === c.id),
                  )}
                  value={view ? view.colorIds : product.colorIds}
                  onChange={(ids) => view && setDraft({ ...view, colorIds: ids })}
                  disabled={!view}
                />
              </div>

              <div className="rounded border border-slate-200 px-3 py-2">
                <div className="mb-1 text-xs font-bold uppercase tracking-wider text-slate-500">
                  Gỗ
                </div>
                <MultiSelectChips
                  variant="chips-plus"
                  options={woods.filter(
                    (c) => c.isActive || selectedWoods.some((s) => s.id === c.id),
                  )}
                  value={view ? view.woodIds : product.woodIds}
                  onChange={(ids) => view && setDraft({ ...view, woodIds: ids })}
                  disabled={!view}
                />
              </div>
            </div>

            {/* Hàng 6: Ghi chú màu sơn liên kết (Mỗi màu sơn 1 ô riêng, ko có thì ko hiện) */}
            {(() => {
              if (view) {
                // Đang trong chế độ chỉnh sửa: hiển thị từng ô cho các màu đã chọn để nhập/sửa
                if (selectedColors.length === 0) return null
                return (
                  <div className="rounded-lg border border-rose-200 bg-rose-50/40 p-3">
                    <div className="mb-2 flex items-center justify-between text-xs font-bold uppercase tracking-wider text-rose-900">
                      <span>Ghi chú màu sơn & Hoàn thiện bề mặt</span>
                      <span className="text-[10px] text-rose-700 font-normal">
                        (Mỗi màu sơn 1 ô riêng)
                      </span>
                    </div>
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                      {selectedColors.map((c) => {
                        const noteVal =
                          draftColorNotes[c.id] !== undefined ? draftColorNotes[c.id] : c.note || ''
                        return (
                          <div key={c.id} className="rounded border border-rose-200 bg-white p-2">
                            <div className="mb-1 flex items-center gap-1.5 text-xs font-bold text-rose-900">
                              <span
                                className="h-2.5 w-2.5 rounded-full ring-1 ring-black/10 shrink-0"
                                style={{ backgroundColor: c.swatchHex || '#f43f5e' }}
                              />
                              <span>{c.name}</span>
                              <span className="text-[10px] font-mono text-rose-600">({c.code})</span>
                            </div>
                            <textarea
                              rows={2}
                              value={noteVal}
                              onChange={(e) =>
                                setDraftColorNotes((prev) => ({ ...prev, [c.id]: e.target.value }))
                              }
                              placeholder={`Ghi chú màu sơn cho ${c.name}...`}
                              className="w-full rounded border border-rose-300 p-1.5 text-xs font-medium text-rose-900 outline-none focus:ring-1 focus:ring-rose-500"
                            />
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )
              }

              // Chế độ xem: Mỗi màu sơn 1 ô riêng (ko có thì ko hiện)
              const colorsWithNotes = selectedColors.filter((c) => (c.note || '').trim().length > 0)
              if (colorsWithNotes.length === 0) return null

              return (
                <div className="rounded-lg border border-rose-200 bg-rose-50/50 p-3">
                  <div className="mb-2 text-xs font-bold uppercase tracking-wider text-rose-900">
                    Ghi chú màu sơn & Hoàn thiện bề mặt
                  </div>
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    {colorsWithNotes.map((c) => (
                      <div key={c.id} className="rounded border border-rose-200 bg-white/90 p-2.5 shadow-2xs">
                        <div className="flex items-center justify-between border-b border-rose-100 pb-1 mb-1.5">
                          <span className="flex items-center gap-1.5 text-xs font-bold text-rose-900">
                            <span
                              className="h-2.5 w-2.5 rounded-full ring-1 ring-black/10 shrink-0"
                              style={{ backgroundColor: c.swatchHex || '#f43f5e' }}
                            />
                            {c.name}
                          </span>
                          <span className="rounded bg-rose-100 px-1.5 py-0.2 text-[10px] font-mono font-semibold text-rose-700">
                            {c.code}
                          </span>
                        </div>
                        <p className="text-xs text-rose-950 whitespace-pre-wrap leading-relaxed">
                          {c.note}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )
            })()}

            {/* Hàng 7: Ghi chú mã hàng */}
            <div className="rounded border border-slate-200 bg-slate-50/60 p-2.5">
              <div className="mb-1 text-xs font-bold uppercase tracking-wider text-slate-500">
                Ghi chú mã hàng
              </div>
              {view ? (
                <textarea
                  rows={2}
                  className={fieldInput}
                  value={view.note}
                  onChange={(e) => setDraft({ ...view, note: e.target.value })}
                  placeholder="Ghi chú người dùng cho mã hàng này..."
                />
              ) : (
                <p className="text-xs text-slate-700 whitespace-pre-wrap">
                  {product.note || '— Tiêu chuẩn kỹ thuật xuất khẩu US'}
                </p>
              )}
            </div>

            {masterCardsCount > 0 && (
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <AttributeNotesPanel
                  title="Ghi chú từ màu (master)"
                  items={selectedColors}
                  onOpenSop={openSopView}
                />
                <AttributeNotesPanel
                  title="Ghi chú từ gỗ (master)"
                  items={selectedWoods}
                  onOpenSop={openSopView}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ---------------- BẢNG CẤU HÌNH THÔNG TIN BOM (THAY THẾ VARIANT CŨ) ---------------- */}
      <section className="mb-5">
        <ProductBomConfigTable
          productCode={product.code}
          configs={bomConfigs}
          availableColors={selectedColors.map((c) => c.name)}
          availableWoods={selectedWoods.map((w) => w.name)}
          onChange={(newConfigs) => {
            setBomConfigs(newConfigs)
            saveProductBomConfigs(product.id, newConfigs)
            void saveProductBomConfigsApi(product.id, newConfigs).catch(() => {})
          }}
          onOpenBom={async (cfg) => {
            setError(null)
            setSelectedBomConfig(cfg)
            // Clear rows before fetch so a failed/slow load cannot keep the previous config's data.
            setBomData({
              productCode: product.code,
              productName: product.name,
              configCode: cfg.code,
              wood: [],
              hardware: [],
            })
            try {
              const loaded = await getProductBomDataApi(product.id, cfg.id)
              setBomData({
                productCode: product.code,
                productName: product.name,
                configCode: cfg.code,
                wood: loaded?.wood ?? [],
                hardware: loaded?.hardware ?? [],
              })
              setViewMode('bom-spreadsheet')
            } catch {
              setBomData(null)
              setError(
                `Không tải được dữ liệu BOM cấu hình «${cfg.code}». Không mở bảng với dữ liệu cũ.`,
              )
            }
          }}
        />
      </section>

      {/* ---------------- BẢNG CẤU HÌNH BOM ĐÓNG GÓI ---------------- */}
      <section className="mb-5">
        <ProductFoamBomConfigTable
          productCode={product.code}
          configs={foamBomConfigs}
          onChange={(newConfigs) => {
            setFoamBomConfigs(newConfigs)
            void saveProductFoamBomConfigsApi(product.id, newConfigs).catch((err) => {
              console.error('Failed to save foam bom configs', err)
            })
          }}
          onOpenBom={async (cfg) => {
            setError(null)
            setFoamBomLines([])
            setSearchParams({ view: 'foam-bom', config: cfg.id })
            try {
              const loaded = await getProductFoamBomDataApi(product.id, cfg.id)
              setFoamBomLines(loaded?.lines ?? [])
              setSelectedFoamBomConfig(cfg)
              setViewMode('foam-bom-spreadsheet')
            } catch {
              setFoamBomLines([])
              setError(
                `Không tải được dữ liệu BOM Đóng Gói cấu hình «${getFoamAssemblyLabel(cfg.assembly_type)} - ${getFoamPackagingLabel(cfg.packaging_type)}».`,
              )
            }
          }}
        />
      </section>

      {/* ---------------- FILE WORKSPACE (8 THƯ MỤC DRAWING, BOM, AI, PACKING... GIỮ NGUYÊN) ---------------- */}
      <section className="rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)] p-4 shadow-xs">
        <div className="mb-2 flex items-center justify-between">
          <h2 className="text-xs font-bold uppercase tracking-wider text-slate-700">
            File Workspace
          </h2>
          <span className="text-[11px] text-slate-400">
            Tải lên và lưu trữ tài liệu kỹ thuật, bản vẽ CAD, file BOM, bao bì
          </span>
        </div>

        <div className="mb-3 grid grid-cols-2 gap-1.5 sm:grid-cols-4 lg:grid-cols-8">
          {PRODUCT_FOLDERS.map((item) => {
            const active = item.slug === workspace.slug
            return (
              <button
                key={item.slug}
                type="button"
                onClick={() => setWorkspaceSlug(item.slug)}
                className={`flex items-center gap-1.5 rounded-[var(--radius-sm)] border px-2 py-2 text-left transition-colors cursor-pointer ${
                  active
                    ? 'border-[var(--color-accent)] bg-[var(--color-accent-soft)] ring-1 ring-emerald-500'
                    : 'border-[var(--color-border)] bg-[var(--color-surface)] hover:border-[var(--color-accent)]'
                }`}
              >
                <Folder className="h-4 w-4 shrink-0 text-amber-500" fill="currentColor" />
                <span className="truncate text-xs font-semibold text-[var(--color-text)]">
                  {item.label.replace(/^\d+\.\s*/, '')}
                </span>
              </button>
            )
          })}
        </div>

        <FileExplorer
          key={`${product.code}-${workspace.slug}`}
          productCode={product.code}
          folderSlug={workspace.slug}
          rootLabel={workspace.label}
        />
      </section>

      {/* Lightbox phóng to ảnh toàn màn hình HD */}
      {lightboxImg && (
        <div
          onClick={() => setLightboxImg(null)}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4 backdrop-blur-xs animate-in fade-in"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="relative max-h-[90vh] max-w-[90vw] overflow-hidden rounded-lg bg-white p-2 shadow-2xl"
          >
            <button
              type="button"
              onClick={() => setLightboxImg(null)}
              className="absolute right-3 top-3 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-slate-900/80 text-white hover:bg-slate-900"
            >
              <X className="h-5 w-5" />
            </button>
            <img
              src={lightboxImg}
              alt="Ảnh phóng to"
              className="max-h-[85vh] max-w-[88vw] object-contain rounded"
            />
            <div className="mt-2 text-center text-xs font-bold text-slate-700">
              Mã hàng: SP-{product.code} — {product.name}
            </div>
          </div>
        </div>
      )}

      {sopPreview ? (
        <SopViewer
          files={sopPreview.files}
          activeId={sopPreview.activeId}
          onClose={() => setSopPreview(null)}
          onSelect={(id) => setSopPreview({ ...sopPreview, activeId: id })}
          allowDownload={false}
        />
      ) : null}

      <Toast message={toastMsg} onDismiss={() => setToastMsg(null)} />
    </div>
  )
}
